import { useMemo, useState, useEffect, useContext, useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useIssueColors,
  getRequestTypeColor,
  useIssueNavigation,
  useIssuesData,
  useLoadingComponents
} from '../hooks';
import useAnalytics from '../hooks/useAnalytics';
import { IssueCard } from './IssueCard';
import { SkeletonList } from './SkeletonList';
import Modal from './Modal';
import AppContext from '../contexts/AppContext';
import { Select, Button, TextField } from '@radix-ui/themes';
import { ReloadIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { isInWebView } from '../utils/deviceUtils';

const defaultFilterState = {
  priority: null,
  requestType: null,
  duedate: null,
  status: null,
  enseigne: null,
  shop: null,
};

const initialFilterState = {
  active: { ...defaultFilterState },
  temp: { ...defaultFilterState }
};

const filterReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TEMP_FILTER':
      return {
        ...state,
        temp: {
          ...state.temp,
          [action.field]: action.value
        }
      };
    case 'APPLY_FILTERS':
      return {
        ...state,
        active: { ...state.temp }
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        active: { ...defaultFilterState },
        temp: { ...defaultFilterState }
      };
    case 'OPEN_MODAL':
      return {
        ...state,
        temp: { ...state.active }
      };
    default:
      return state;
  }
};

const IssuesList = ({
  userRole,
  userName,
  userSectors,
  userServiceDeskName,
}) => {
  useAnalytics({
    currentUser: userName,
    project: userServiceDeskName,
    role: userRole,
    module: 'view-issues',
  });

  const { t } = useTranslation();
  const { serviceDesk, currentUser } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;
  const { getStatusColor, getRequestTypeColor } = useIssueColors();
  const { handleNavigateToIssue } = useIssueNavigation();
  const { ErrorDisplay, EmptyState } = useLoadingComponents();
  const deviceIsInWebView = isInWebView();
  const [activeTab, setActiveTab] = useState('open');
  const [ownerScope, setOwnerScope] = useState('mine');
  const [filterState, dispatchFilter] = useReducer(filterReducer, initialFilterState);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    issuesOpen,
    issuesPending,
    issuesDraft,
    issuesToValidate,
    issuesClosed,
    issuesArchived,
    loading,
    error,
    refetchOpen,
    refetchClosed,
    refetchArchived,
    refetchPending,
    refetchToValidate,
    refetchDraft,
  } = useIssuesData({ userRole, serviceDeskConfig });

  const allIssues = useMemo(() => [
    ...(issuesOpen || []),
    ...(issuesPending || []),
    ...(issuesDraft || []),
    ...(issuesToValidate || []),
    ...(issuesClosed || []),
    ...(issuesArchived || [])
  ], [issuesOpen, issuesPending, issuesDraft, issuesToValidate, issuesClosed, issuesArchived]);

  const availablePriorities = useMemo(() => {
    const priorities = [];
    allIssues.forEach(issue => {
      if (issue.fields.priority.name && !priorities.includes(issue.fields.priority.name)) {
        priorities.push(issue.fields.priority.name);
      }
    });
    return priorities;
  }, [allIssues]);

  const availableRequestTypes = useMemo(() => {
    const requestTypes = [];
    allIssues.forEach(issue => {
      if (issue.mappedFields.requestType?.requestType?.name && !requestTypes.includes(issue.mappedFields.requestType?.requestType?.name)) {
        requestTypes.push(issue.mappedFields.requestType?.requestType?.name);
      }
    });
    return requestTypes;
  }, [allIssues]);

  const availableStatuses = useMemo(() => {
    const statuses = [];
    allIssues.forEach(issue => {
      if (issue.fields?.status?.name && !statuses.includes(issue.fields.status.name)) {
        statuses.push(issue.fields.status.name);
      }
    });
    return statuses.sort();
  }, [allIssues]);

  const availableEnseignes = useMemo(() => {
    const enseignes = new Set();
    allIssues.forEach(issue => {
      const shopSign = issue.mappedFields?.shopSign;
      if (shopSign) {
        if (Array.isArray(shopSign)) {
          shopSign.forEach(sign => {
            if (sign?.value) enseignes.add(sign.value);
          });
        } else if (shopSign?.value) {
          enseignes.add(shopSign.value);
        }
      }
    });
    return Array.from(enseignes).sort();
  }, [allIssues]);

  const availableShops = useMemo(() => {
    const shops = new Set();
    allIssues.forEach(issue => {
      const interventionPoint = issue.mappedFields?.intervention_point;
      if (interventionPoint) {
        if (Array.isArray(interventionPoint)) {
          interventionPoint.forEach(point => {
            if (point?.value) shops.add(point.value);
          });
        } else if (interventionPoint?.value) {
          shops.add(interventionPoint.value);
        }
      }
    });
    return Array.from(shops).sort();
  }, [allIssues]);

  const isCreatedByMe = useCallback((issue) => {
    const reporter = issue.fields?.reporter;

    if (!reporter)
      return false;

    const myAccountId = currentUser?.accountId;
    const myEmail = currentUser?.email?.toLowerCase();

    if (myAccountId && reporter.accountId === myAccountId)
      return true;

    if (myEmail && reporter.emailAddress?.toLowerCase() === myEmail)
      return true;

    return false;
  }, [currentUser]);

  // Derive which owner kinds actually exist in the data, so the toggle is
  // only shown when both "mine" and "others" issues are present.
  const { hasMineIssues, hasOthersIssues } = useMemo(() => ({
    hasMineIssues: allIssues.some((issue) => isCreatedByMe(issue)),
    hasOthersIssues: allIssues.some((issue) => !isCreatedByMe(issue)),
  }), [allIssues, isCreatedByMe]);

  const showOwnerToggle = hasMineIssues && hasOthersIssues;

  // When a single kind exists, force that kind regardless of the selected tab
  // so we never render an empty list for an irrelevant ownerScope.
  const effectiveOwnerScope = showOwnerToggle ? ownerScope : (hasMineIssues ? 'mine' : 'others');

  const applyFilters = (issuesList) => {
    let filtered = [...issuesList];

    filtered = filtered.filter(issue => (effectiveOwnerScope === 'mine' ? isCreatedByMe(issue) : !isCreatedByMe(issue)));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      filtered = filtered.filter(issue => {
        const titleMatch = String(issue.mappedFields?.title || '').toLowerCase().includes(query);
        const keyMatch = String(issue.key || '').toLowerCase().includes(query);
        const interventionPointValue = Array.isArray(issue.mappedFields?.intervention_point)
          ? issue.mappedFields?.intervention_point[0]?.value || ''
          : issue.mappedFields?.intervention_point?.value || '';
        const interventionPointMatch = interventionPointValue.toLowerCase().includes(query);
        const requestTypeMatch = (issue.mappedFields?.requestType?.requestType?.name || '').toLowerCase().includes(query);

        return titleMatch ||
          keyMatch ||
          interventionPointMatch ||
          requestTypeMatch;
      });
    }

    if (filterState.active.priority) {
      filtered = filtered.filter(issue =>
        issue.fields?.priority?.name === filterState.active.priority
      );
    }

    if (filterState.active.requestType) {
      filtered = filtered.filter(issue =>
        issue.mappedFields?.requestType?.requestType?.name === filterState.active.requestType
      );
    }

    if (filterState.active.status) {
      filtered = filtered.filter(issue =>
        issue.fields?.status?.name === filterState.active.status
      );
    }

    if (filterState.active.enseigne) {
      filtered = filtered.filter(issue => {
        const shopSign = issue.mappedFields?.shopSign;
        if (!shopSign) return false;

        if (Array.isArray(shopSign)) {
          return shopSign.some(sign => sign?.value === filterState.active.enseigne);
        }
        return shopSign?.value === filterState.active.enseigne;
      });
    }

    if (filterState.active.shop) {
      filtered = filtered.filter(issue => {
        const interventionPoint = issue.mappedFields?.intervention_point;
        if (!interventionPoint) return false;

        if (Array.isArray(interventionPoint)) {
          return interventionPoint.some(point => point?.value === filterState.active.shop);
        }
        return interventionPoint?.value === filterState.active.shop;
      });
    }

    if (filterState.active.duedate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(issue => {
        const duedate = issue.fields?.duedate;
        if (!duedate) return false;

        const dueDate = new Date(duedate + 'T00:00:00');
        if (isNaN(dueDate.getTime())) return false;
        dueDate.setHours(0, 0, 0, 0);

        switch (filterState.active.duedate) {
          case 'today':
            return dueDate.getTime() === today.getTime();
          case 'thisWeek':
            const weekEnd = new Date(today);
            weekEnd.setDate(today.getDate() + (7 - today.getDay()));
            return dueDate.getTime() >= today.getTime() && dueDate.getTime() <= weekEnd.getTime();
          case 'thisMonth':
            const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            monthEnd.setHours(23, 59, 59, 999);
            return dueDate.getTime() >= today.getTime() && dueDate.getTime() <= monthEnd.getTime();
          case 'overdue':
            return dueDate.getTime() < today.getTime();
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredIssuesOpen = useMemo(() => applyFilters(issuesOpen || []), [issuesOpen, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesPending = useMemo(() => applyFilters(issuesPending || []), [issuesPending, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesDraft = useMemo(() => applyFilters(issuesDraft || []), [issuesDraft, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesToValidate = useMemo(() => applyFilters(issuesToValidate || []), [issuesToValidate, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesClosed = useMemo(() => applyFilters(issuesClosed || []), [issuesClosed, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesArchived = useMemo(() => applyFilters(issuesArchived || []), [issuesArchived, filterState.active, searchQuery, effectiveOwnerScope]);

  const issues = useMemo(() => {
    let filteredIssues = activeTab === 'open' ? filteredIssuesOpen :
      (activeTab === 'pending' ? filteredIssuesPending :
      (activeTab === 'draft' ? filteredIssuesDraft :
      (activeTab === 'toValidate' ? filteredIssuesToValidate :
      (activeTab === 'archived' ? filteredIssuesArchived :
      filteredIssuesClosed))));

    return filteredIssues.sort((a, b) => new Date(b.updated) - new Date(a.updated));
  }, [activeTab, filteredIssuesOpen, filteredIssuesPending, filteredIssuesDraft, filteredIssuesToValidate, filteredIssuesClosed, filteredIssuesArchived]);

  useEffect(() => {
    if (issuesPending.length > 0) setActiveTab('pending');
    else if (issuesDraft.length > 0) setActiveTab('draft');
  }, [issuesPending, issuesDraft]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 'closed' && issuesClosed == null) refetchClosed();
    if (tab === 'archived' && issuesArchived == null) refetchArchived();
    if (tab === 'open' && issuesOpen == null) refetchOpen();
    if (tab === 'pending' && issuesPending == null) refetchPending();
    if (tab === 'draft' && issuesDraft == null) refetchDraft();
    if (tab === 'toValidate' && issuesToValidate == null) refetchToValidate();
  };

  const isMultiSectors = userSectors[0] === '*' || userSectors.length > 1;

  const handleOpenFilterModal = () => {
    dispatchFilter({ type: 'OPEN_MODAL' });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    dispatchFilter({ type: 'APPLY_FILTERS' });
    setIsFilterModalOpen(false);
  };

  const handleCancelFilters = () => {
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    dispatchFilter({ type: 'RESET_FILTERS' });
  };

  const activeFiltersCount = [
    filterState.active.priority,
    filterState.active.requestType,
    filterState.active.duedate,
    filterState.active.status,
    filterState.active.enseigne,
    filterState.active.shop
  ].filter(Boolean).length;

  return (
    <div className="container mx-auto py-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('My tickets')}</h1>
        <div className="mt-2 text-sm text-gray-600">
          {isMultiSectors ? t('Sectors:') : t('Sector:')} {userSectors[0] === '*' ? t('Whole territory') :
            userSectors.length > 10
              ? `${userSectors.slice(0, 3).join(', ')}, ... , ${userSectors.slice(-3).join(', ')}`
              : userSectors.sort((a, b) => a.length - b.length || a.localeCompare(b)).join(', ')
          }
        </div>

        {/* Champ de recherche */}
        <div className="mt-4">
          <TextField.Root
            size="3"
            placeholder={t('Search…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
            {searchQuery && (
              <TextField.Slot>
                <button
                  onClick={() => setSearchQuery('')}
                  className="hover:bg-gray-200 rounded-full p-1 transition-colors"
                  aria-label={t('Clear search')}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </TextField.Slot>
            )}
          </TextField.Root>
        </div>
      </div>

      {/* Segmented control Mes demandes / Autres tickets — only when both kinds exist */}
      {showOwnerToggle && (
        <div className="mb-4 inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          <button
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${ownerScope === 'mine' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setOwnerScope('mine')}
          >
            {t('My requests')}
          </button>
          <button
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${ownerScope === 'others' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setOwnerScope('others')}
          >
            {t('Other tickets')}
          </button>
        </div>
      )}

      {/* Tabs et bouton filtre */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-x-2 flex-wrap">
          {issuesDraft.length > 0 && (
            <button
              className={`${activeTab === 'draft' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
              onClick={() => handleTabChange('draft')}
            >
              {t('Drafts')} {filteredIssuesDraft?.length ? `(${filteredIssuesDraft.length})` : ''}
            </button>
          )}
          {issuesPending.length > 0 && (
            <button
              className={`${activeTab === 'pending' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
              onClick={() => handleTabChange('pending')}
            >
              {t('To complete')} {filteredIssuesPending?.length ? `(${filteredIssuesPending.length})` : ''}
            </button>
          )}
          {issuesToValidate.length > 0 && (
            <button
              className={`${activeTab === 'toValidate' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
              onClick={() => handleTabChange('toValidate')}
            >
              {t('To validate')} {filteredIssuesToValidate?.length ? `(${filteredIssuesToValidate.length})` : ''}
            </button>
          )}
          <button
            className={`${activeTab === 'open' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
            onClick={() => handleTabChange('open')}
          >
            {t('In progress')} {filteredIssuesOpen?.length ? `(${filteredIssuesOpen.length})` : ''}
          </button>
          <button
            className={`${activeTab === 'closed' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
            onClick={() => handleTabChange('closed')}
          >
            {t('Closed')} {filteredIssuesClosed?.length ? `(${filteredIssuesClosed.length})` : ''}
          </button>
          {serviceDeskConfig?.issueValidation?.statusIdArchived && (
            <button
              className={`${activeTab === 'archived' ? 'border-black text-black font-bold' : 'border-transparent text-gray-600 hover:text-gray-900'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
              onClick={() => handleTabChange('archived')}
            >
              {t('Archived')} {filteredIssuesArchived?.length ? `(${filteredIssuesArchived.length})` : ''}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {(filterState.active.priority || filterState.active.requestType || filterState.active.duedate || filterState.active.status || filterState.active.enseigne || filterState.active.shop) && (
            <Button
              variant="ghost"
              size="2"
              onClick={handleResetFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              <ReloadIcon className="w-4 h-4 text-gray-500" />
            </Button>
          )}
          <Modal
            open={isFilterModalOpen}
            onOpenChange={setIsFilterModalOpen}
            onApply={handleApplyFilters}
            onCancel={handleCancelFilters}
            title={t('Filters')}
            description={t('Filter tickets by priority, type, status, banner, store and due date')}
            trigger={
              <Button
                size="2"
                onClick={handleOpenFilterModal}
                className="!bg-black !text-white hover:!bg-gray-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                </svg>
                {activeFiltersCount > 0 && (
                  <span className="ml-2 bg-white text-black rounded-full px-2 py-0.5 text-xs font-medium">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            }
          >
            <div className="space-y-4">
              {/* Filtre par priorité */}
              {availablePriorities.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Priority')}</label>
                  <Select.Root
                    value={filterState.temp.priority || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'priority',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                        {availablePriorities.map(priority => (
                          <Select.Item key={priority} value={priority}>
                            {priority}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par requestType */}
              {availableRequestTypes.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Type')}</label>
                  <Select.Root
                    value={filterState.temp.requestType || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'requestType',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableRequestTypes.map(requestType => (
                          <Select.Item key={requestType} value={requestType}>
                            {requestType}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par statut */}
              {availableStatuses.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Status')}</label>
                  <Select.Root
                    value={filterState.temp.status || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'status',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableStatuses.map(status => (
                          <Select.Item key={status} value={status}>
                            {status}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par enseigne */}
              {availableEnseignes.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Banner')}</label>
                  <Select.Root
                    value={filterState.temp.enseigne || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'enseigne',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                        {availableEnseignes.map(enseigne => (
                          <Select.Item key={enseigne} value={enseigne}>
                            {enseigne}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par magasin */}
              {availableShops.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Store')}</label>
                  <Select.Root
                    value={filterState.temp.shop || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'shop',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableShops.map(shop => (
                          <Select.Item key={shop} value={shop}>
                            {shop}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par duedate */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('Due date')}</label>
                <Select.Root
                  value={filterState.temp.duedate || "all"}
                  onValueChange={(value) => dispatchFilter({
                    type: 'SET_TEMP_FILTER',
                    field: 'duedate',
                    value: value === "all" ? null : value
                  })}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Group>
                      <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                      <Select.Item value="today">{t('Today')}</Select.Item>
                      <Select.Item value="thisWeek">{t('This week')}</Select.Item>
                      <Select.Item value="thisMonth">{t('This month')}</Select.Item>
                      <Select.Item value="overdue">{t('Overdue')}</Select.Item>
                    </Select.Group>
                  </Select.Content>
                </Select.Root>
              </div>
            </div>
          </Modal>
        </div>
      </div>

      <div>
        {loading && issues.length === 0 ? (
          <SkeletonList numberOfItems={6} />
        ) : error && issues.length === 0 ? (
          <ErrorDisplay error={error} onRetry={() => (activeTab === 'closed' ? refetchClosed() : refetchOpen())} />
        ) : (!issues || issues.length === 0) ? (
          <EmptyState
            message={t('No ticket found')}
            description={activeTab === 'closed' ? t('No ticket has been closed yet.') : t('No ticket in progress.')}
          />
        ) : (
          <div className={`space-y-2 ${!deviceIsInWebView ? 'pb-20' : ''}`}>
            {issues.sort((a, b) => new Date(b.fields.updated) - new Date(a.fields.updated)).map((issue) => {
              const statusColor = issue.fields.statusCategory?.colorName || getStatusColor(issue.fields.status);

              const requestTypeColor = getRequestTypeColor(issue.mappedFields.requestType?.requestType);

              return (
                <div key={issue.key} className="space-y-2">
                  <IssueCard
                    color={requestTypeColor}
                    issueKey={issue.key}
                    createdAt={issue.fields.created}
                    dueDate={issue.fields.duedate}
                    status={issue.fields.status.name}
                    statusColor={statusColor}
                    summary={issue.mappedFields.title}
                    interventionPoint={issue.mappedFields.intervention_point}
                    onClick={() => handleNavigateToIssue(issue.key, issue)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default IssuesList;
